import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  useMediaQuery,
  useTheme,
  Chip,
  TextField
} from '@mui/material';
import {
  Person as PersonIcon,
  PhotoLibrary as GalleryIcon,
  Share as ShareIcon,
  Link as LinkIcon,
  ExpandMore as ExpandMoreIcon,
  CameraAlt as CameraAltIcon,
  Visibility as VisibilityIcon,
  ContentCopy as CopyIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Business as BusinessIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';

// Import des composants
import ProfileHeader from './profile/ProfileHeader';
import PhotoSection from './profile/PhotoSection';
import ProfileForm from './profile/ProfileForm';
import ShareSection from './profile/ShareSection';
import PhotoUploadDialog from './profile/PhotoUploadDialog';
import QRCodeModal from './profile/QRCodeModal';

const ProfileTab = ({ userProfile, setSuccess, setError, currentUser: user, targetSection, centralServices, setCentralServices }) => {
  const [formData, setFormData] = useState({
    salonName: userProfile.salonName || '',
    ownerName: userProfile.ownerName || '',
    phone: userProfile.phone || '',
    address: userProfile.address || '',
    city: userProfile.city || '',
    region: userProfile.region || '',
    category: userProfile.category || '',
    description: userProfile.description || '',
    googleMyBusinessUrl: userProfile.googleMyBusinessUrl || ''
  });

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expandedSection, setExpandedSection] = useState('photos');
  const [showScrollArrow, setShowScrollArrow] = useState(true);
  const [qrModalOpen, setQrModalOpen] = useState(false);

  const { currentUser } = useAuth();
  const activeUser = user || currentUser;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Handle scroll arrow visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Hide arrow when near bottom of page
      if (scrollTop + windowHeight >= documentHeight - 100) {
        setShowScrollArrow(false);
      } else {
        setShowScrollArrow(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle target section navigation
  useEffect(() => {
    if (targetSection) {
      // Map sections to accordion panels
      const sectionToPanel = {
        'photos': 'photos',
        'profile': 'info',
        'services': 'services'
      };
      
      const panelToExpand = sectionToPanel[targetSection];
      if (panelToExpand) {
        setExpandedSection(panelToExpand);
        
        // Scroll to section after a short delay to allow accordion to expand
        setTimeout(() => {
          const element = document.getElementById(`section-${targetSection}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    }
  }, [targetSection]);

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
    if (location) {
      setFormData(prev => ({
        ...prev,
        city: location.city || '',
        region: location.state || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Prepare the data to save
      const dataToSave = {
        ...formData,
        // Convert services text to array if it contains line breaks, otherwise keep as string
        services: formData.services ? formData.services.split('\n').filter(line => line.trim()) : [],
        updatedAt: new Date()
      };
      
      await updateDoc(userRef, dataToSave);

      setSuccess('Profil mis à jour avec succès!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner une image (JPG, PNG, etc.)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('L\'image ne doit pas dépasser 5MB');
        return;
      }

      setSelectedFile(file);
      setPhotoDialogOpen(true);
    }
  };

  const uploadPhoto = async () => {
    if (!selectedFile) return;

    setUploadingPhoto(true);
    try {
      const filename = `salon-photos/${currentUser.uid}/${Date.now()}-${selectedFile.name}`;
      const storageRef = ref(storage, filename);

      await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(storageRef);

      const currentPhotos = userProfile.photos || [];
      const updatedPhotos = [...currentPhotos, downloadURL];

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        photos: updatedPhotos,
        updatedAt: new Date()
      });

      setSuccess('Photo ajoutée avec succès!');
      setPhotoDialogOpen(false);
      setSelectedFile(null);
      
      window.location.reload();
    } catch (error) {
      console.error('Error uploading photo:', error);
      setError('Erreur lors de l\'upload de la photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const deletePhoto = async (photoUrl, index) => {
    try {
      // First update the database to remove the photo from the array
      const updatedPhotos = userProfile.photos.filter((_, i) => i !== index);
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        photos: updatedPhotos,
        updatedAt: new Date()
      });

      // Then try to delete from storage (if this fails, it's not critical)
      try {
        const photoRef = ref(storage, photoUrl);
        await deleteObject(photoRef);
      } catch (storageError) {
        console.warn('Could not delete photo from storage (may already be deleted):', storageError);
        // Don't throw error here - the photo was removed from the database successfully
      }

      // Show success message
      const isMainPhoto = index === 0;
      const successMessage = isMainPhoto && updatedPhotos.length > 0
        ? 'Photo principale supprimée! La photo suivante est maintenant votre photo principale.'
        : 'Photo supprimée avec succès!';
      
      setSuccess(successMessage);
      setTimeout(() => setSuccess(''), 5000);
      
      // Refresh to show changes
      window.location.reload();
    } catch (error) {
      console.error('Error deleting photo:', error);
      setError('Erreur lors de la suppression de la photo');
      setTimeout(() => setError(''), 5000);
    }
  };

  const setMainPhoto = async (newMainIndex) => {
    try {
      const photos = [...userProfile.photos];
      const [mainPhoto] = photos.splice(newMainIndex, 1);
      const reorderedPhotos = [mainPhoto, ...photos];

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        photos: reorderedPhotos,
        updatedAt: new Date()
      });

      setSuccess('Photo principale mise à jour!');
      setTimeout(() => setSuccess(''), 3000);
      
      // Refresh to show the change
      window.location.reload();
    } catch (error) {
      console.error('Error setting main photo:', error);
      setError('Erreur lors de la mise à jour de la photo principale');
    }
  };

  const updateProfilePhoto = async (photoBlob) => {
    if (!photoBlob) return;

    setUploadingPhoto(true);
    try {
      const filename = `profile-images/${currentUser.uid}/profile-${Date.now()}.jpg`;
      const storageRef = ref(storage, filename);

      await uploadBytes(storageRef, photoBlob);
      const downloadURL = await getDownloadURL(storageRef);

      // Delete old profile photo if exists
      if (userProfile.photo) {
        try {
          const oldPhotoRef = ref(storage, userProfile.photo);
          await deleteObject(oldPhotoRef);
        } catch (error) {
          console.log('Old profile photo not found or already deleted');
        }
      }

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        photo: downloadURL,
        updatedAt: new Date()
      });

      setSuccess('Photo de profil mise à jour avec succès!');
      setTimeout(() => setSuccess(''), 3000);
      
      // Refresh the page to show the new photo
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile photo:', error);
      setError('Erreur lors de la mise à jour de la photo de profil');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const copyToClipboard = () => {
    const publicLink = `${window.location.origin}/salon/${activeUser?.uid}`;
    navigator.clipboard.writeText(publicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const openPublicProfile = () => {
    const publicLink = `${window.location.origin}/salon/${activeUser?.uid}`;
    window.open(publicLink, '_blank');
  };

  const shareViaEmail = () => {
    const publicLink = `${window.location.origin}/salon/${activeUser?.uid}`;
    const subject = encodeURIComponent(`Découvrez ${userProfile.salonName}`);
    const body = encodeURIComponent(
      `Bonjour,\n\nJe vous invite à découvrir mon salon ${userProfile.salonName}.\n\nVous pouvez consulter nos services et prendre rendez-vous directement en ligne :\n${publicLink}\n\nÀ bientôt !`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaWhatsApp = () => {
    const publicLink = `${window.location.origin}/salon/${activeUser?.uid}`;
    const text = encodeURIComponent(
      `Découvrez ${userProfile.salonName} et prenez rendez-vous en ligne : ${publicLink}`
    );
    window.open(`https://wa.me/?text=${text}`);
  };

  const shareViaFacebook = () => {
    const publicLink = `${window.location.origin}/salon/${activeUser?.uid}`;
    const url = encodeURIComponent(publicLink);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
  };

  const handleQRCodeClick = () => {
    setQrModalOpen(true);
  };

  const photoCount = userProfile.photos?.length || 0;
  const maxPhotos = 10;

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  if (isMobile) {
    // VERSION MOBILE OPTIMISÉE
    return (
      <Box sx={{ pb: 10 }}>
        {/* Header Mobile */}
        <ProfileHeader
          userProfile={userProfile}
          isMobile={isMobile}
          copied={copied}
          onOpenPublicProfile={openPublicProfile}
          onCopyToClipboard={copyToClipboard}
          onUpdatePhoto={updateProfilePhoto}
          onQRCodeClick={handleQRCodeClick}
        />

        {/* Sections en accordéon pour mobile */}
        <Box>
          {/* Section Photos */}
          <Accordion
            id="section-photos"
            expanded={expandedSection === 'photos'}
            onChange={handleAccordionChange('photos')}
            sx={{
              mb: 1,
              background: ' #faf8f3'
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <GalleryIcon color="primary" />
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Photos du salon
                </Typography>
                <Chip
                  label={`${photoCount}/${maxPhotos}`}
                  onClick={() => {}}
                  size="small"
                  color={photoCount === 0 ? 'error' : photoCount < 3 ? 'warning' : 'success'}
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <PhotoSection 
                userProfile={userProfile}
                isMobile={isMobile}
                onFileSelect={handleFileSelect}
                onDeletePhoto={deletePhoto}
                onSetMainPhoto={setMainPhoto}
              />
            </AccordionDetails>
          </Accordion>

          {/* Section Informations */}
          <Accordion
            id="section-profile"
            expanded={expandedSection === 'info'}
            onChange={handleAccordionChange('info')}
            sx={{
              mb: 1,
              background: 'linear-gradient(135deg, #f5f5f4 0%, #ffffff 100%)'
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6">
                  Informations du salon
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <ProfileForm
                formData={formData}
                selectedLocation={selectedLocation}
                loading={loading}
                isMobile={isMobile}
                onInputChange={handleInputChange}
                onLocationChange={handleLocationChange}
                onSubmit={handleSubmit}
                targetSection={targetSection}
              />
            </AccordionDetails>
          </Accordion>


          {/* Section Partage */}
          <Accordion 
            expanded={expandedSection === 'share'} 
            onChange={handleAccordionChange('share')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShareIcon color="primary" />
                <Typography variant="h6">
                  Partager mon salon
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <ShareSection 
                activeUser={activeUser}
                userProfile={userProfile}
                copied={copied}
                onCopyToClipboard={copyToClipboard}
                onShareViaEmail={shareViaEmail}
                onShareViaWhatsApp={shareViaWhatsApp}
                onShareViaFacebook={shareViaFacebook}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Floating Action Button pour ajouter photo */}
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000
          }}
          onClick={() => document.getElementById('photo-upload').click()}
        >
          <CameraAltIcon />
        </Fab>

        {/* Photo Upload Dialog */}
        <PhotoUploadDialog
          open={photoDialogOpen}
          selectedFile={selectedFile}
          uploadingPhoto={uploadingPhoto}
          onClose={() => setPhotoDialogOpen(false)}
          onUpload={uploadPhoto}
        />

        {/* QR Code Modal */}
        <QRCodeModal
          open={qrModalOpen}
          onClose={() => setQrModalOpen(false)}
          salonUrl={`${window.location.origin}/salon/${activeUser?.uid}`}
          salonName={userProfile?.salonName}
          onCopyToClipboard={copyToClipboard}
        />
      </Box>
    );
  }

  // VERSION DESKTOP
  return (
    <Box>
      {/* Profile Header */}
      <ProfileHeader
        userProfile={userProfile}
        isMobile={isMobile}
        copied={copied}
        onOpenPublicProfile={openPublicProfile}
        onCopyToClipboard={copyToClipboard}
        onUpdatePhoto={updateProfilePhoto}
        onQRCodeClick={handleQRCodeClick}
      />

      {/* Photos Section Desktop */}
      <Card sx={{ 
        mb: 4
      }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            Photos de votre salon
            <Chip 
              onClick={() => {}}
              label={`${photoCount}/${maxPhotos}`} 
              size="small" 
              variant="outlined"
              color={photoCount === 0 ? 'error' : photoCount < 3 ? 'warning' : 'success'}
            />
          </Typography>
          
          <PhotoSection 
            userProfile={userProfile}
            isMobile={isMobile}
            onFileSelect={handleFileSelect}
            onDeletePhoto={deletePhoto}
            onSetMainPhoto={setMainPhoto}
          />
        </CardContent>
      </Card>

      <Grid container spacing={4}>
        {/* Profile Information Form */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                Informations de votre salon
              </Typography>

              <ProfileForm
                formData={formData}
                selectedLocation={selectedLocation}
                loading={loading}
                isMobile={isMobile}
                onInputChange={handleInputChange}
                onLocationChange={handleLocationChange}
                onSubmit={handleSubmit}
                targetSection={targetSection}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions Sidebar */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Public Link Card */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinkIcon />
                  Votre lien public
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Partagez ce lien avec vos clients pour qu'ils découvrent votre salon et prennent rendez-vous.
                </Typography>

                <ShareSection 
                  activeUser={activeUser}
                  userProfile={userProfile}
                  copied={copied}
                  onCopyToClipboard={copyToClipboard}
                  onShareViaEmail={shareViaEmail}
                  onShareViaWhatsApp={shareViaWhatsApp}
                  onShareViaFacebook={shareViaFacebook}
                />
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Scroll Arrow */}
      {showScrollArrow && (
        <Fab
          color="primary"
          size="small"
          onClick={scrollToBottom}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1000,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark'
            }
          }}
        >
          <ArrowDownIcon />
        </Fab>
      )}

      {/* Photo Upload Dialog */}
      <PhotoUploadDialog
        open={photoDialogOpen}
        selectedFile={selectedFile}
        uploadingPhoto={uploadingPhoto}
        onClose={() => setPhotoDialogOpen(false)}
        onUpload={uploadPhoto}
      />

      {/* QR Code Modal */}
      <QRCodeModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        salonUrl={`${window.location.origin}/salon/${activeUser?.uid}`}
        salonName={userProfile?.salonName}
        onCopyToClipboard={copyToClipboard}
      />
    </Box>
  );
};

export default ProfileTab;
